import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

interface LegalPageProps {
  title: string;
  content: React.ReactNode;
}

export const LegalPage: React.FC<LegalPageProps> = ({ title, content }) => (
  <div className="min-h-screen bg-[#f8fafc] py-24 px-4">
    <div className="max-w-4xl mx-auto">
      <Link to="/" className="inline-flex items-center text-chui-navy font-black text-xs uppercase tracking-widest mb-12 hover:translate-x-[-4px] transition-transform">
        <ArrowLeft size={14} className="mr-2" /> Back to Home
      </Link>
      <div className="bg-white p-10 md:p-16 rounded-[3rem] border border-slate-200 shadow-sm text-left">
        <div className="flex items-center space-x-4 mb-10">
          <div className="w-12 h-12 bg-chui-navy rounded-2xl flex items-center justify-center text-chui-yellow">
            <Shield size={24} />
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-chui-navy uppercase tracking-tighter">{title}</h1>
        </div>
        <div className="prose prose-slate max-w-none prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tight prose-p:text-slate-700 prose-p:font-bold prose-p:leading-relaxed text-sm">
          {content}
        </div>
      </div>
    </div>
  </div>
);

export const TermsContent = () => (
  <div className="space-y-12">
    <section>
      <h2 className="text-xl mb-4">1. INTRODUCTION AND ACCEPTANCE</h2>
      <p>These Terms of Service constitute a legally binding agreement between you and CHUI Business Credit, a financial technology company registered under Kenyan law. By using our subscription-based credit partnership, you agree to these Terms.</p>
    </section>
    
    <section>
      <h2 className="text-xl mb-4">2. DEFINITIONS</h2>
      <ul className="list-disc pl-5 space-y-3">
        <li><strong>"Agent"</strong>: Authorized CHUI representatives facilitating applications and providing ongoing support</li>
        <li><strong>"Stewardship Model"</strong>: CHUI's partnership approach that intensifies support during business challenges</li>
        <li><strong>"Subscription Fee"</strong>: One-time upfront payment for 180-day credit access</li>
        <li><strong>"Tranche"</strong>: Credit disbursements of 10-day cycles (18 total over 180 days)</li>
      </ul>
    </section>

    <section>
      <h2 className="text-xl mb-4">3. ELIGIBILITY</h2>
      <ul className="list-disc pl-5 space-y-3">
        <li>Minimum 18 years of age</li>
        <li>Kenyan citizen or legal resident</li>
        <li>Operating legitimate business with verifiable daily turnover</li>
        <li>Valid M-Pesa number for disbursements and repayments</li>
        <li>Not providing false information (subject to permanent disqualification)</li>
      </ul>
    </section>

    <section>
      <h2 className="text-xl mb-4">4. SUBSCRIPTION-BASED CREDIT PARTNERSHIP</h2>
      <div className="space-y-6">
        <div>
          <h3 className="font-black text-sm uppercase mb-2">4.1 Tier Structure:</h3>
          <ul className="space-y-2">
            <li><strong>Tier 1:</strong> KES 1,500+ daily sales → KES 4,750 subscription → KES 5,000 per tranche → KES 90,000 total</li>
            <li><strong>Tier 2:</strong> KES 2,500+ daily sales → KES 9,500 subscription → KES 10,000 per tranche → KES 180,000 total</li>
            <li><strong>Tier 3:</strong> KES 4,000+ daily sales → KES 14,250 subscription → KES 15,000 per tranche → KES 270,000 total</li>
            <li><strong>Tier 4:</strong> KES 5,000+ daily sales → KES 19,000 subscription → KES 20,000 per tranche → KES 360,000 total</li>
            <li><strong>Tier 5:</strong> KES 6,500+ daily sales → KES 23,750 subscription → KES 25,000 per tranche → KES 450,000 total</li>
          </ul>
        </div>
        <div>
          <h3 className="font-black text-sm uppercase mb-2">4.2 How It Works:</h3>
          <ul className="list-disc pl-5 space-y-2">
            <li>Pay subscription fee upfront (non-refundable)</li>
            <li>Receive credit in 18 tranches over 180 days (every 10 days)</li>
            <li>Make daily repayments (KES 500-2,500 based on tier)</li>
            <li>Complete full 180-day cycle for maximum value</li>
          </ul>
        </div>
      </div>
    </section>

    <section>
      <h2 className="text-xl mb-4">5. REPAYMENT AND STEWARDSHIP</h2>
      <ul className="list-disc pl-5 space-y-3">
        <li><strong>Daily Repayments:</strong> Via M-Pesa to designated CHUI number</li>
        <li><strong>Late Payment Consequence:</strong> Delayed next tranche disbursement only</li>
        <li><strong>No Penalties:</strong> No late fees, penalty interest, or additional charges</li>
        <li><strong>Stewardship Activation:</strong> Support intensifies when challenges arise</li>
      </ul>
    </section>

    <section>
      <h2 className="text-xl mb-4">6. STEWARDSHIP TIMELINE</h2>
      <ul className="list-disc pl-5 space-y-3">
        <li><strong>Days 1-14:</strong> Self-correction phase with tranche delays</li>
        <li><strong>Day 15:</strong> Agent outreach and business health assessment</li>
        <li><strong>Days 15-30:</strong> Light support and collaborative catch-up planning</li>
        <li><strong>Days 30-60:</strong> Business restructuring with payment pauses if needed</li>
        <li><strong>Days 60-90:</strong> Deep partnership support and recovery planning</li>
        <li><strong>90+ Days:</strong> Mutual partnership assessment with graceful exit option</li>
      </ul>
    </section>

    <section>
      <h2 className="text-xl mb-4">7. WHAT CHUI NEVER DOES</h2>
      <ul className="list-disc pl-5 space-y-3">
        <li>Report to Credit Reference Bureaus at any stage</li>
        <li>Charge late fees or penalty interest</li>
        <li>Take legal action or seize assets</li>
        <li>Harass customers or families</li>
        <li>Abandon customers during business struggles</li>
      </ul>
    </section>
  </div>
);

export const PrivacyContent = () => (
  <div className="space-y-12">
    <section>
      <h2 className="text-xl mb-4">1. INTRODUCTION</h2>
      <p>CHUI protects your privacy under Kenya's Data Protection Act, 2019, while delivering partnership-based credit services to Kenya's MSME community.</p>
    </section>
    
    <section>
      <h2 className="text-xl mb-4">2. INFORMATION WE COLLECT</h2>
      <div className="space-y-4">
        <div>
          <h3 className="font-black text-sm uppercase">Personal Data:</h3>
          <p>Identity information (name, national ID, phone, address), M-Pesa number and transaction data, business information (type, location, turnover, photos), subscription and tranche records.</p>
        </div>
        <div>
          <h3 className="font-black text-sm uppercase">Partnership Data:</h3>
          <p>Business support and restructuring records, stewardship intervention records, community network activations, and long-term growth tracking.</p>
        </div>
      </div>
    </section>

    <section>
      <h2 className="text-xl mb-4">3. HOW WE USE INFORMATION</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li><strong>Primary:</strong> Tier qualification, subscription processing, tranche disbursements.</li>
        <li><strong>Stewardship:</strong> Business health monitoring, support service delivery.</li>
        <li><strong>Growth:</strong> Tier progression assessment, community network building.</li>
      </ul>
    </section>

    <section>
      <h2 className="text-xl mb-4">4. DATA SHARING</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li><strong>We NEVER share with:</strong> Credit Reference Bureaus.</li>
        <li><strong>We MAY share with:</strong> Partner MFIs, training providers (with explicit consent only).</li>
        <li><strong>We WILL share if:</strong> Required by law (court orders, regulatory investigations).</li>
      </ul>
    </section>

    <section>
      <h2 className="text-xl mb-4">5. DATA RETENTION</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li><strong>Subscription and partnership data:</strong> 7 years after relationship ends.</li>
        <li><strong>Business support records:</strong> 5 years for continuous improvement.</li>
        <li><strong>Transaction records:</strong> 10 years per regulatory requirements.</li>
      </ul>
    </section>

    <section>
      <h2 className="text-xl mb-4">6. YOUR RIGHTS</h2>
      <p>You have the right to access, rectify, or delete personal data (subject to legal requirements), restrict processing, data portability, and withdraw consent. Complaints can be filed with the Office of Data Protection Commissioner.</p>
    </section>
  </div>
);

export const DisputesContent = () => (
  <div className="space-y-12">
    <section>
      <h2 className="text-xl mb-4">1. PARTNERSHIP APPROACH TO DISPUTES</h2>
      <p>CHUI views disputes as partnership challenges requiring collaborative solutions, not adversarial proceedings.</p>
    </section>

    <section>
      <h2 className="text-xl mb-4">2. SCOPE OF PARTNERSHIP CHALLENGES</h2>
      <p>Covers subscription fee calculations, tranche timing, stewardship intervention appropriateness, business support quality, and restructuring effectiveness.</p>
    </section>

    <section>
      <h2 className="text-xl mb-4">3. RESOLUTION PROCESS</h2>
      <div className="space-y-4">
        <div>
          <h3 className="font-black text-sm uppercase">Step 1: Guardian Discussion (24-hour response)</h3>
          <p>Direct contact with your CHUI Agent/Guardian. Focus on understanding and collaborative solutions.</p>
        </div>
        <div>
          <h3 className="font-black text-sm uppercase">Step 2: Team Lead Involvement (5 business days)</h3>
          <p>Escalation to local Team Lead. Business visit if needed. Comprehensive situation assessment.</p>
        </div>
        <div>
          <h3 className="font-black text-sm uppercase">Step 3: Management Partnership Review (10 business days)</h3>
          <p>Senior management involvement. Complete relationship review and restructuring options.</p>
        </div>
        <div>
          <h3 className="font-black text-sm uppercase">Step 4: Mediation (If needed)</h3>
          <p>Voluntary mediation focusing on preserving the long-term partnership. Costs shared between parties.</p>
        </div>
      </div>
    </section>

    <section>
      <h2 className="text-xl mb-4">4. EXTERNAL CHANNELS</h2>
      <p>AMFI Kenya, Central Bank of Kenya, and the legal system remain available but are rarely needed due to our partnership approach.</p>
    </section>
  </div>
);

export const StewardshipContent = () => (
  <div className="space-y-12">
    <section>
      <h2 className="text-xl mb-4">1. PARTNERSHIP PHILOSOPHY</h2>
      <p>CHUI operates as a growth partner, not a traditional lender. When payment challenges arise, our response is enhanced support, not punishment.</p>
    </section>

    <section>
      <h2 className="text-xl mb-4">2. STEWARDSHIP ESCALATION</h2>
      <div className="space-y-4 text-slate-700">
        <p><strong>Days 1-14 (Self-Correction):</strong> Each late day delays the next tranche by one day. No penalties.</p>
        <p><strong>Day 15 (Partnership Activation):</strong> Agent reaches out to understand root causes. Pure support orientation.</p>
        <p><strong>Days 15-30 (Light Touch Support):</strong> Catch-up planning or initial business diagnostic.</p>
        <p><strong>Days 30-60 (Active Restructuring):</strong> Payment pause (14-30 days) while implementng revenue pivots or linkage to additional capital.</p>
        <p><strong>Days 60-90 (Deep Partnership):</strong> Extended pause for major transitions. Full business transformation support.</p>
        <p><strong>90+ Days (Mutual Assessment):</strong> Evaluation of partnership viability. Graceful exit option if needed with no penalties or CRB reporting.</p>
      </div>
    </section>

    <section>
      <h2 className="text-xl mb-4">3. SUPPORT SERVICES PROVIDED</h2>
      <p>Business diagnostics, revenue stream development, market linkage, skills training, and connection to additional capital sources (MFIs, SACCOs).</p>
    </section>

    <section>
      <h2 className="text-xl mb-4">4. WHAT DISTINGUISHES CHUI</h2>
      <p>Support intensifies during challenges. No punitive measures ever. Commitment regardless of temporary setbacks. Part as friends, not adversaries.</p>
    </section>

    <section>
      <h2 className="text-xl mb-4">5. CUSTOMER RIGHTS DURING CHALLENGES</h2>
      <p>Right to enhanced support, payment pauses, skills training, and a graceful exit without penalties. Maintenance of dignity is paramount.</p>
    </section>

    <section>
      <h2 className="text-xl mb-4">6. TIER PROGRESSION OPPORTUNITY</h2>
      <p>Successful completion with 90%+ on-time payment record qualifies for higher tier access, better terms, and entry into the CHUI entrepreneur community network.</p>
    </section>
  </div>
);
